<script setup lang="ts">
import { ref, computed, onMounted, watch, type Ref } from 'vue'
import AppLayout from '../layouts/AppLayout.vue'
import FacetGroup from '../components/workspace/FacetGroup.vue'
import StarRating from '../components/StarRating.vue'
import { fetchAdvisorSuppliers, type AdvisorSupplierCard, type PeopleSearchResult } from '../services/supplierApi'
import { useAuth } from '../composables/useAuth'
import { useRouter } from 'vue-router'
import SupplierLogo from '../components/SupplierLogo.vue'
import SupplierPreferenceActions from '../components/SupplierPreferenceActions.vue'
import { useSupplierPreferencesStore } from '../stores/supplierPreferences'
import { useUserStore } from '../stores/user'
import { trackEvent } from '../services/analytics'
import CustomSelect from '../components/CustomSelect.vue'
import { useFuzzySupplierSearch } from '../composables/useFuzzySupplierSearch'

const { getAccessTokenSilently } = useAuth()
const router = useRouter()
const prefsStore = useSupplierPreferencesStore()
const userStore = useUserStore()
const { loadSupplierNames, fuzzySearchSuppliers } = useFuzzySupplierSearch()

const suppliers = ref<AdvisorSupplierCard[]>([])
const people = ref<PeopleSearchResult[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const searchQuery = ref('')
const fuzzySuggestions = ref<string[]>([])
const usingFuzzyResults = ref(false)
let searchDebounce: ReturnType<typeof setTimeout> | null = null

// Filters & Pagination
// Default to 'My TSDs' if the advisor has TSD affiliations, otherwise 'All'
const userTsdNames = [...userStore.advisorTsdNames]
const userTsdNameSet = new Set(userTsdNames.map(n => n.toLowerCase()))
const tsdFilter = ref(userTsdNames.length > 0 ? 'My TSDs' : 'All')
const tsdOptions = userTsdNames.length > 0
    ? ['My TSDs', 'All', ...userTsdNames]
    : ['All']

// Filter the TSD badges shown on each supplier card based on the active tsdFilter.
// 'My TSDs' → only show TSDs the user is affiliated with
// A specific TSD name → only show that TSD
// 'All' → show every TSD the supplier is affiliated with
function visibleTsdsForSupplier(affiliations: { id: string; name: string }[] | null | undefined): { id: string; name: string }[] {
    if (!affiliations || affiliations.length === 0) return []
    if (tsdFilter.value === 'All') return affiliations
    if (tsdFilter.value === 'My TSDs') {
        return affiliations.filter(a => userTsdNameSet.has(a.name.toLowerCase()))
    }
    const target = tsdFilter.value.toLowerCase()
    return affiliations.filter(a => a.name.toLowerCase() === target)
}

const page = ref(1)
// Load the full population so faceting + filtering operate over all suppliers, not one page.
// Client-side "Load more" (shown) controls how many cards actually render.
const limit = ref<number | 'all'>('all')
const totalSuppliers = ref(0)

// Compare mode state
const compareMode = ref(false)
const selectedSupplierIds = ref<string[]>(['', '', ''])
const allSuppliersForCompare = ref<AdvisorSupplierCard[]>([])

const canCompare = computed(() => {
    const filled = selectedSupplierIds.value.filter(id => id !== '')
    const unique = new Set(filled)
    return filled.length >= 2 && unique.size === filled.length
})

async function toggleCompareMode() {
    compareMode.value = !compareMode.value
    compareSearches.value = ['', '', '']
    if (!compareMode.value) {
        selectedSupplierIds.value = ['', '', '']
    } else if (allSuppliersForCompare.value.length === 0) {
        // Load the FULL directory for the compare dropdowns — deliberately
        // unfiltered. Passing the page's tsd filter broke this: the default
        // "My TSDs" pseudo-value went to the backend verbatim, matched no
        // TSD, and returned an empty pool (nothing selectable, compare dead).
        // Comparing should work across any suppliers regardless of filters.
        try {
            const token = await getAccessTokenSilently()
            const { suppliers: all } = await fetchAdvisorSuppliers({ limit: 'all' }, token)
            allSuppliersForCompare.value = all?.length ? all : suppliers.value
        } catch (e) {
            console.error('Failed to load suppliers for compare:', e)
            allSuppliersForCompare.value = suppliers.value // fallback to current page
        }
    }
}

// Compare search/filter
const compareSearches = ref<string[]>(['', '', ''])
function filteredCompareSuppliers(idx: number) {
    const q = compareSearches.value[idx]?.toLowerCase().trim()
    const list = allSuppliersForCompare.value
    if (!q) return list.slice(0, 30)
    return list.filter(s => s.name.toLowerCase().includes(q)).slice(0, 30)
}
function selectCompareSupplier(idx: number, supplier: AdvisorSupplierCard) {
    selectedSupplierIds.value[idx] = supplier.id
    compareSearches.value[idx] = supplier.name
}
function clearCompareSupplier(idx: number) {
    selectedSupplierIds.value[idx] = ''
    compareSearches.value[idx] = ''
}

function goToComparison() {
    const ids = selectedSupplierIds.value.filter(id => id !== '')
    if (ids.length >= 2) {
        trackEvent('supplier_compare_initiated', {
            supplier_count: ids.length,
        })
        router.push({ path: '/advisor/suppliers/compare', query: { ids: ids.join(',') } })
    }
}

async function loadSuppliers() {
    loading.value = true
    error.value = null
    fuzzySuggestions.value = []
    usingFuzzyResults.value = false
    try {
        const token = await getAccessTokenSilently()
        // Resolve 'My TSDs' to comma-separated list of user's TSD names
        const tsdParam = tsdFilter.value === 'My TSDs'
            ? userTsdNames.join(',')
            : tsdFilter.value
        const result = await fetchAdvisorSuppliers({
            search: searchQuery.value,
            tsd: tsdParam,
            page: page.value,
            limit: limit.value
        }, token)

        suppliers.value = result.suppliers
        totalSuppliers.value = result.total
        people.value = result.people || []

        // If exact search returned no results, try fuzzy matching
        if (searchQuery.value && result.total === 0) {
            const fuzzy = fuzzySearchSuppliers(searchQuery.value)
            if (fuzzy.ids.length > 0) {
                fuzzySuggestions.value = fuzzy.suggestions
                // Load fuzzy-matched suppliers from the server (paginated)
                const fuzzyResult = await fetchAdvisorSuppliers({
                    ids: fuzzy.ids,
                    tsd: tsdParam,
                    page: page.value,
                    limit: limit.value
                }, token)
                suppliers.value = fuzzyResult.suppliers
                totalSuppliers.value = fuzzyResult.total
                usingFuzzyResults.value = true
            }
        }

        if (searchQuery.value) {
            trackEvent('supplier_directory_searched', {
                query: searchQuery.value,
                result_count: result.suppliers.length,
                tsd_filter: tsdFilter.value,
                fuzzy_fallback: usingFuzzyResults.value,
            })
        }
    } catch (e) {
        console.error(e)
        error.value = "Unable to load supplier directory."
    } finally {
        loading.value = false
    }
}

// Debounced search
watch(searchQuery, () => {
    page.value = 1 // Reset to first page on search
    if (searchDebounce) clearTimeout(searchDebounce)
    searchDebounce = setTimeout(() => {
        loadSuppliers()
    }, 400)
})

// Watch filters
watch([tsdFilter, limit], () => {
    page.value = 1
    loadSuppliers()
})

// Watch page
watch(page, () => {
    loadSuppliers()
})

// Filter out blocked suppliers
const visibleSuppliers = computed(() =>
    suppliers.value.filter(s => !prefsStore.isBlocked(s.id))
)

onMounted(async () => {
    loadSuppliers()
    if (!prefsStore.loaded) prefsStore.load()
    // Load fuzzy search index in background
    try {
        const token = await getAccessTokenSilently()
        await loadSupplierNames(token)
    } catch { /* non-critical */ }
})

const navigateToProfile = (id: string) => {
    router.push(`/advisor/suppliers/${id}`)
}

// ── Console directory facets / view (client-side over the full loaded population) ──
const view = ref<'grid' | 'list'>('list')
const selectedCats = ref<Set<string>>(new Set())
const selectedTsds = ref<Set<string>>(new Set())
const selectedSegments = ref<Set<string>>(new Set())
const selectedIndustries = ref<Set<string>>(new Set())
const selectedGeos = ref<Set<string>>(new Set())
const selectedOwnership = ref<Set<string>>(new Set())
const selectedRevenue = ref<Set<string>>(new Set())
const networkOnly = ref(false)
const promoOnly = ref(false)
const minRating = ref(0)
const sortBy = ref<'name' | 'rating' | 'reviews' | 'channel'>('name')
const ratingThresholds = [
  { value: 0, label: 'Any rating' },
  { value: 4, label: '★ 4.0 & up' },
  { value: 3, label: '★ 3.0 & up' },
  { value: 2, label: '★ 2.0 & up' },
]
function toggleSet(set: Ref<Set<string>>, v: string) { const n = new Set(set.value); n.has(v) ? n.delete(v) : n.add(v); set.value = n }
function toggleCat(c: string) { toggleSet(selectedCats, c) }
function toggleTsdFacet(t: string) { toggleSet(selectedTsds, t) }
function toggleSegment(v: string) { toggleSet(selectedSegments, v) }
function toggleIndustry(v: string) { toggleSet(selectedIndustries, v) }
function toggleGeo(v: string) { toggleSet(selectedGeos, v) }
function toggleOwnership(v: string) { toggleSet(selectedOwnership, v) }
function toggleRevenue(v: string) { toggleSet(selectedRevenue, v) }
function supCats(s: AdvisorSupplierCard): string[] { return s.primaryCategories || [] }
// The 8 canonical solution areas. Only show a category chip when it's one of these —
// hides legacy "Area > Subcategory" composites or other odd values.
const CANONICAL_AREAS = new Set([
  'Cloud & Managed Services', 'Contact Center & CX', 'Cybersecurity Lifecycle (NIST-Style)',
  'Expense Management & Operational Services', 'IoT, Edge & Smart Environments',
  'Mobility, Wireless & Devices', 'Networking, SASE & Connectivity', 'UCaaS, CPaaS & Voice',
])
function displayCat(s: AdvisorSupplierCard): string {
  return (s.primaryCategories || []).find(c => CANONICAL_AREAS.has(c)) || ''
}
const segLabel = (v: string) => v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

function facetOf(pick: (s: AdvisorSupplierCard) => string[]) {
  const m = new Map<string, number>()
  for (const s of suppliers.value) for (const v of pick(s)) { if (v) m.set(v, (m.get(v) || 0) + 1) }
  return [...m.entries()].sort((a, b) => b[1] - a[1])
}
const categoryFacets = computed(() => facetOf(supCats))
const tsdFacets = computed(() => facetOf(s => (s.tsdAffiliations || []).map(t => t.name)))
const segmentFacets = computed(() => facetOf(s => s.primarySegments || []))
const industryFacets = computed(() => facetOf(s => s.primaryIndustries || []))
const geoFacets = computed(() => facetOf(s => s.geographicCoverage || []))
const ownershipFacets = computed(() => facetOf(s => (s.ownershipType ? [s.ownershipType] : [])))
const revenueFacets = computed(() => facetOf(s => (s.annualRevenueRange ? [s.annualRevenueRange] : [])))
const networkTotal = computed(() => suppliers.value.filter(s => visibleTsdsForSupplier(s.tsdAffiliations).length > 0).length)
const promoTotal = computed(() => suppliers.value.filter(s => s.hasActivePromo).length)

const activeFacetCount = computed(() =>
  selectedCats.value.size + selectedTsds.value.size + selectedSegments.value.size +
  selectedIndustries.value.size + selectedGeos.value.size + selectedOwnership.value.size +
  selectedRevenue.value.size + (networkOnly.value ? 1 : 0) + (promoOnly.value ? 1 : 0) + (minRating.value ? 1 : 0))
function clearFacets() {
  selectedCats.value = new Set(); selectedTsds.value = new Set(); selectedSegments.value = new Set()
  selectedIndustries.value = new Set(); selectedGeos.value = new Set(); selectedOwnership.value = new Set()
  selectedRevenue.value = new Set(); networkOnly.value = false; promoOnly.value = false; minRating.value = 0
}

const directoryRows = computed(() => visibleSuppliers.value.filter(s => {
  if (networkOnly.value && !visibleTsdsForSupplier(s.tsdAffiliations).length) return false
  if (promoOnly.value && !s.hasActivePromo) return false
  if (selectedCats.value.size && !supCats(s).some(c => selectedCats.value.has(c))) return false
  if (selectedTsds.value.size && !(s.tsdAffiliations || []).some(t => selectedTsds.value.has(t.name))) return false
  if (selectedSegments.value.size && !(s.primarySegments || []).some(v => selectedSegments.value.has(v))) return false
  if (selectedIndustries.value.size && !(s.primaryIndustries || []).some(v => selectedIndustries.value.has(v))) return false
  if (selectedGeos.value.size && !(s.geographicCoverage || []).some(v => selectedGeos.value.has(v))) return false
  if (selectedOwnership.value.size && !(s.ownershipType && selectedOwnership.value.has(s.ownershipType))) return false
  if (selectedRevenue.value.size && !(s.annualRevenueRange && selectedRevenue.value.has(s.annualRevenueRange))) return false
  if (minRating.value && (s.aggregateRating || 0) < minRating.value) return false
  return true
}))

// Sort the filtered population (before client-side render pagination).
const sortedRows = computed(() => {
  const rows = [...directoryRows.value]
  if (sortBy.value === 'rating') rows.sort((a, b) => (b.aggregateRating || 0) - (a.aggregateRating || 0) || (b.aggregateRatingCount || 0) - (a.aggregateRatingCount || 0))
  else if (sortBy.value === 'reviews') rows.sort((a, b) => (b.aggregateRatingCount || 0) - (a.aggregateRatingCount || 0))
  else if (sortBy.value === 'channel') rows.sort((a, b) => (b.channelInvestmentScore || 0) - (a.channelInvestmentScore || 0))
  else rows.sort((a, b) => a.name.localeCompare(b.name))
  return rows
})

// Client-side render pagination so we never paint the whole population at once.
const shown = ref(60)
const pagedRows = computed(() => sortedRows.value.slice(0, shown.value))
watch([searchQuery, selectedCats, selectedTsds, selectedSegments, selectedIndustries, selectedGeos, selectedOwnership, selectedRevenue, networkOnly, promoOnly, minRating, sortBy], () => { shown.value = 60 })
function scoreClass(v?: number): string { const n = v || 0; return n >= 7 ? 'hi' : n >= 4 ? 'md' : 'lo' }
function scoreFill(v?: number): string { const c = scoreClass(v); return c === 'hi' ? 'var(--cc-good)' : c === 'md' ? 'var(--cc-warn)' : 'var(--cc-bad)' }
function quoteSupplier(s: AdvisorSupplierCard, e: Event) { e.stopPropagation(); const u = s.directQuoteUrl; if (u) window.open(u, '_blank'); else navigateToProfile(s.id) }
</script>

<template>
  <AppLayout>
    <div class="cc-dir">
      <!-- Header -->
      <div class="cc-dir-head">
        <div>
          <div class="cc-dir-meta">
            <span style="width:6px;height:6px;border-radius:50%;background:var(--cc-live);box-shadow:0 0 6px var(--cc-live)"></span>
            <span>SUPPLIERS · DIRECTORY</span>
          </div>
          <h1>Supplier Directory</h1>
          <p>Discover partners, training, and exclusive promotions across {{ (totalSuppliers || suppliers.length).toLocaleString() }}+ suppliers.</p>
        </div>
        <div class="cc-dir-head-r">
          <button class="cc-dir-pill" :class="{ 'is-on': compareMode }" @click="toggleCompareMode">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h13M3 12h13M3 18h13M21 6l-2 2 2 2M21 12l-2 2 2 2M21 18l-2 2 2 2"/></svg>
            {{ compareMode ? 'CANCEL COMPARE' : 'COMPARE' }}<em v-if="!compareMode"> · {{ selectedSupplierIds.filter(Boolean).length }}</em>
          </button>
        </div>
      </div>

      <!-- Compare panel (preserved) -->
      <div v-if="compareMode" class="bg-cc-primary-subtle border border-cc-border rounded-cc p-5">
        <div class="flex flex-col lg:flex-row lg:items-end gap-4">
          <div class="flex-1">
            <h3 class="text-sm font-semibold text-cc-text mb-3">Select up to 3 suppliers to compare</h3>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div v-for="(_, idx) in selectedSupplierIds" :key="idx" class="relative">
                <label class="block text-xs font-medium text-cc-primary mb-1">Supplier {{ idx + 1 }}{{ idx < 2 ? ' *' : ' (optional)' }}</label>
                <div class="relative">
                  <input v-model="compareSearches[idx]" type="text" :placeholder="selectedSupplierIds[idx] ? '' : 'Search suppliers...'" autocomplete="off" class="block w-full px-3 pr-8 py-2 border border-cc-border rounded-cc bg-cc-elevated text-cc-text text-sm focus:ring-2 focus:ring-cc-primary outline-none" @focus="compareSearches[idx] = compareSearches[idx] || ''" />
                  <button v-if="selectedSupplierIds[idx]" @click="clearCompareSupplier(idx)" type="button" class="absolute inset-y-0 right-0 pr-2 flex items-center text-cc-text-tertiary hover:text-cc-danger"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg></button>
                </div>
                <div v-if="!selectedSupplierIds[idx] && compareSearches[idx] !== undefined" class="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto bg-cc-elevated border border-cc-border rounded-cc shadow-cc-dropdown">
                  <button v-for="s in filteredCompareSuppliers(idx)" :key="s.id" type="button" :disabled="selectedSupplierIds.includes(s.id)" @mousedown.prevent="selectCompareSupplier(idx, s)" class="w-full text-left px-3 py-1.5 text-sm transition-colors" :class="selectedSupplierIds.includes(s.id) ? 'text-cc-text-secondary opacity-50 cursor-not-allowed' : 'text-cc-text hover:bg-cc-hover'">{{ s.name }}</button>
                </div>
              </div>
            </div>
          </div>
          <button @click="goToComparison" :disabled="!canCompare" class="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-cc transition-colors whitespace-nowrap" :class="canCompare ? 'bg-cc-primary text-cc-btn-text hover:opacity-90' : 'bg-cc-hover text-cc-text-secondary cursor-not-allowed'">Compare Selected</button>
        </div>
      </div>

      <!-- Stat strip -->
      <div class="cc-dir-stats">
        <div class="cc-dir-stat">
          <div class="cc-dir-stat-l"><span class="cc-label">TOTAL · DIRECTORY</span><div class="cc-dir-stat-num">{{ (totalSuppliers || suppliers.length).toLocaleString() }}</div></div>
          <div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-accent)"><polyline points="0,22 16,16 32,18 48,9 64,4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div>
        </div>
        <div class="cc-dir-stat is-network">
          <div class="cc-dir-stat-l"><span class="cc-label">IN NETWORK · MINE</span><div class="cc-dir-stat-num">{{ networkTotal }}</div></div>
          <div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-accent)"><polyline points="0,20 16,18 32,14 48,10 64,6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div>
        </div>
        <div class="cc-dir-stat is-promo">
          <div class="cc-dir-stat-l"><span class="cc-label">WITH ACTIVE PROMO</span><div class="cc-dir-stat-num">{{ promoTotal }}</div></div>
          <div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-warn)"><polyline points="0,18 16,20 32,10 48,12 64,5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div>
        </div>
        <div class="cc-dir-stat">
          <div class="cc-dir-stat-l"><span class="cc-label">SHOWING · NOW</span><div class="cc-dir-stat-num">{{ directoryRows.length }}</div></div>
          <div class="cc-dir-stat-spark"><svg viewBox="0 0 64 28" preserveAspectRatio="none" style="width:64px;height:28px;color:var(--cc-accent-2)"><polyline points="0,14 16,12 32,16 48,11 64,9" fill="none" stroke="currentColor" stroke-width="1.5"/></svg></div>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="cc-dir-bar">
        <div class="cc-dir-search">
          <span class="cc-dir-search-ico"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16zM21 21l-4.3-4.3"/></svg></span>
          <input v-model="searchQuery" placeholder="Search suppliers, products, people…" />
        </div>
        <CustomSelect v-model="tsdFilter" :options="tsdOptions.map(o => ({ value: o, label: o === 'All' ? 'All TSDs' : o }))" class="w-40" />
        <CustomSelect v-model="sortBy" :options="[{ value: 'name', label: 'Name A–Z' }, { value: 'rating', label: 'Highest rated' }, { value: 'reviews', label: 'Most reviews' }, { value: 'channel', label: 'Channel score' }]" class="w-40" />
        <button class="cc-dir-pill" :class="{ 'is-on': networkOnly }" @click="networkOnly = !networkOnly">
          <svg v-if="networkOnly" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>
          IN NETWORK
        </button>
        <button class="cc-dir-pill" :class="{ 'is-on': promoOnly }" @click="promoOnly = !promoOnly">
          <svg v-if="promoOnly" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>
          PROMO ACTIVE
        </button>
        <div class="cc-dir-view">
          <button :class="{ on: view === 'list' }" @click="view = 'list'" title="List"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg></button>
          <button :class="{ on: view === 'grid' }" @click="view = 'grid'" title="Grid"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"/></svg></button>
        </div>
      </div>

      <!-- Body: facet rail + results -->
      <div class="cc-dir-grid">
        <aside class="cc-dir-facets">
          <div class="cc-dir-facet-card">
          <div class="cc-dir-facets-top">
            <span class="cc-label">Refine</span>
            <button v-if="activeFacetCount" class="cc-dir-facets-clear" @click="clearFacets">Clear {{ activeFacetCount }}</button>
          </div>
          <div class="cc-dir-rating-filter">
            <div class="cc-dir-rating-filter-h">Minimum rating</div>
            <div class="cc-dir-rating-opts">
              <button v-for="rt in ratingThresholds" :key="rt.value" class="cc-dir-rating-opt" :class="{ 'is-on': minRating === rt.value }" @click="minRating = rt.value">{{ rt.label }}</button>
            </div>
          </div>
          <FacetGroup v-if="categoryFacets.length" title="Category" :items="categoryFacets" :selected="selectedCats" :default-open="true" @toggle="toggleCat" />
          <FacetGroup v-if="industryFacets.length" title="Industry" :items="industryFacets" :selected="selectedIndustries" @toggle="toggleIndustry" />
          <FacetGroup v-if="segmentFacets.length" title="Segment" :items="segmentFacets" :selected="selectedSegments" :label-format="segLabel" @toggle="toggleSegment" />
          <FacetGroup v-if="geoFacets.length" title="Geography" :items="geoFacets" :selected="selectedGeos" @toggle="toggleGeo" />
          <FacetGroup v-if="tsdFacets.length" title="TSD Partner" :items="tsdFacets" :selected="selectedTsds" @toggle="toggleTsdFacet" />
          <FacetGroup v-if="revenueFacets.length" title="Revenue" :items="revenueFacets" :selected="selectedRevenue" @toggle="toggleRevenue" />
          <FacetGroup v-if="ownershipFacets.length" title="Ownership" :items="ownershipFacets" :selected="selectedOwnership" :label-format="segLabel" @toggle="toggleOwnership" />
          </div>
        </aside>

        <div style="min-width:0">
          <!-- Loading -->
          <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div v-for="i in 8" :key="i" class="bg-cc-elevated border border-cc-border rounded-cc-lg h-56 animate-pulse"></div>
          </div>
          <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-4 rounded-cc text-center">{{ error }}</div>

          <template v-else>
            <!-- Fuzzy banner (preserved) -->
            <div v-if="usingFuzzyResults && fuzzySuggestions.length > 0" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-cc p-4 mb-4 flex items-start gap-3">
              <svg class="w-5 h-5 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              <div>
                <p class="text-sm text-amber-800 dark:text-amber-200">No exact match for "<span class="font-semibold">{{ searchQuery }}</span>". Showing similar:</p>
                <div class="flex flex-wrap gap-2 mt-2"><button v-for="suggestion in fuzzySuggestions" :key="suggestion" @click="searchQuery = suggestion" class="text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-2.5 py-1 rounded-md">{{ suggestion }}</button></div>
              </div>
            </div>

            <!-- People (preserved, compact) -->
            <div v-if="people.length > 0 && searchQuery" class="bg-cc-elevated border border-cc-border cc-bezel rounded-cc-lg p-4 mb-4">
              <div class="cc-label mb-3" style="color:var(--cc-text-3)">PEOPLE · {{ people.length }}</div>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <div v-for="person in people" :key="person.id" @click="navigateToProfile(person.supplier.id)" class="flex items-center gap-2 p-2 rounded-cc border border-cc-border hover:bg-cc-hover cursor-pointer">
                  <span class="text-sm text-cc-text font-medium">{{ person.name }}</span><span class="text-xs text-cc-text-tertiary">· {{ person.supplier.name }}</span>
                </div>
              </div>
            </div>

            <!-- GRID -->
            <div v-if="view === 'grid'" class="cc-dir-cards">
              <div v-for="s in pagedRows" :key="s.id" class="cc-dir-card" :class="{ 'is-promo': s.hasActivePromo }" @click="navigateToProfile(s.id)">
                <div class="cc-dir-card-head">
                  <div class="h-10 w-10 bg-white rounded-cc flex items-center justify-center overflow-hidden shrink-0"><SupplierLogo :src="s.logoUrl" :website="s.websiteUrl" :alt="s.name" class="object-contain max-h-full max-w-full" /></div>
                  <div class="cc-dir-card-id">
                    <div class="cc-dir-card-name">{{ s.name }}<span v-if="s.hasActivePromo" class="promo-tag">PROMO</span><span v-if="prefsStore.isFavorite(s.id)" class="text-cc-warning" title="Favorite"><svg class="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20"><path d="M9.05 2.93c.3-.92 1.6-.92 1.9 0l1.36 4.18a1 1 0 00.95.69h4.4c.97 0 1.37 1.24.59 1.81l-3.56 2.59a1 1 0 00-.36 1.12l1.36 4.18c.3.92-.76 1.69-1.54 1.12l-3.56-2.59a1 1 0 00-1.18 0l-3.56 2.59c-.78.57-1.84-.2-1.54-1.12l1.36-4.18a1 1 0 00-.36-1.12L1.4 9.61c-.78-.57-.38-1.81.59-1.81h4.4a1 1 0 00.95-.69l1.36-4.18z"/></svg></span></div>
                    <div class="cc-dir-card-cat"><span v-if="displayCat(s)" class="cc-dir-chip">{{ displayCat(s) }}</span><span v-if="(s.tsdAffiliations||[]).length" class="cc-dir-card-tsds">{{ (s.tsdAffiliations||[]).map(t => t.name).join(' · ') }}</span></div>
                  </div>
                  <div @click.stop><SupplierPreferenceActions :supplier-id="s.id" :compact="true" /></div>
                </div>
                <p class="cc-dir-card-desc">{{ s.shortDescription }}</p>
                <div class="cc-dir-card-scores">
                  <div class="cc-dir-scoring"><span class="cc-dir-score-key">CH</span><span class="cc-dir-score-val" :class="scoreClass(s.channelInvestmentScore)">{{ s.channelInvestmentScore ?? '—' }}<em style="opacity:.5;font-size:8px">/10</em></span><span class="cc-dir-score-bar"><span :style="{ width: ((s.channelInvestmentScore||0)/10*100)+'%', display:'block', height:'100%', background: scoreFill(s.channelInvestmentScore) }"></span></span></div>
                  <div class="cc-dir-scoring"><span class="cc-dir-score-key">PD</span><span class="cc-dir-score-val" :class="scoreClass(s.productDepthScore)">{{ s.productDepthScore ?? '—' }}<em style="opacity:.5;font-size:8px">/10</em></span><span class="cc-dir-score-bar"><span :style="{ width: ((s.productDepthScore||0)/10*100)+'%', display:'block', height:'100%', background: scoreFill(s.productDepthScore) }"></span></span></div>
                  <div class="cc-dir-scoring"><span class="cc-dir-score-key">FS</span><span class="cc-dir-score-val" :class="scoreClass(s.financialStabilityScore)">{{ s.financialStabilityScore ?? '—' }}<em style="opacity:.5;font-size:8px">/10</em></span><span class="cc-dir-score-bar"><span :style="{ width: ((s.financialStabilityScore||0)/10*100)+'%', display:'block', height:'100%', background: scoreFill(s.financialStabilityScore) }"></span></span></div>
                </div>
                <div class="cc-dir-card-foot">
                  <div class="cc-dir-card-stat"><span class="cc-dir-card-stat-n">{{ s.productCount }}</span><span class="cc-dir-card-stat-l">Products</span></div>
                  <div class="cc-dir-card-stat"><span class="cc-dir-card-stat-n">{{ s.resourceCount }}</span><span class="cc-dir-card-stat-l">Resources</span></div>
                  <div v-if="s.aggregateRating" class="cc-dir-card-stat updated"><StarRating :rating="s.aggregateRating" :count="s.aggregateRatingCount" :size="13" :show-count="false" :numbers-only="true" /><span class="cc-dir-card-stat-l">{{ s.aggregateRatingCount ? s.aggregateRatingCount.toLocaleString() + ' reviews' : 'Rating' }}</span></div>
                  <div v-else class="cc-dir-card-stat updated"><span class="cc-dir-card-stat-n">—</span><span class="cc-dir-card-stat-l">Rating</span></div>
                  <!-- Hidden for now; drop v-if to restore --><button v-if="false" class="cc-dir-card-quote" @click="quoteSupplier(s, $event)">QUOTE</button>
                </div>
              </div>
            </div>

            <!-- LIST -->
            <div v-else class="cc-dir-list">
              <div class="cc-dir-headrow" style="grid-template-columns: 40px 1.4fr 1fr 124px 128px 104px">
                <span></span><span>SUPPLIER</span><span>CATEGORY · TSDs</span><span style="text-align:center">CH · PD · FS</span><span style="text-align:center">RATING</span><span style="text-align:right; padding-left:13px">ACTIONS</span>
              </div>
              <div v-for="s in pagedRows" :key="s.id" class="cc-dir-row" style="grid-template-columns: 40px 1.4fr 1fr 124px 128px 104px" :class="{ 'is-promo': s.hasActivePromo }" @click="navigateToProfile(s.id)">
                <div class="h-9 w-9 bg-white rounded-cc flex items-center justify-center overflow-hidden"><SupplierLogo :src="s.logoUrl" :website="s.websiteUrl" :alt="s.name" class="object-contain max-h-full max-w-full" /></div>
                <div style="min-width:0"><div class="cc-dir-row-name">{{ s.name }}<span v-if="s.hasActivePromo" class="promo-tag">PROMO</span></div><div class="cc-dir-row-desc">{{ s.shortDescription }}</div></div>
                <div class="cc-dir-row-meta"><span v-if="displayCat(s)" class="cc-dir-chip">{{ displayCat(s) }}</span> <span class="cc-dir-card-tsds">{{ (s.tsdAffiliations||[]).map(t => t.name).join(' · ') }}</span></div>
                <div class="flex items-center justify-center gap-1 num" style="font-size:11px"><span :style="{ color: scoreFill(s.channelInvestmentScore) }">{{ s.channelInvestmentScore ?? '—' }}</span>·<span :style="{ color: scoreFill(s.productDepthScore) }">{{ s.productDepthScore ?? '—' }}</span>·<span :style="{ color: scoreFill(s.financialStabilityScore) }">{{ s.financialStabilityScore ?? '—' }}</span></div>
                <div style="display:flex; justify-content:center;"><StarRating v-if="s.aggregateRating" :rating="s.aggregateRating" :count="s.aggregateRatingCount" :size="13" :show-count="false" :numbers-only="true" /><span v-else class="num" style="font-size:11px; color: var(--cc-text-3)">—</span></div>
                <div class="cc-dir-row-actions flex items-center justify-end gap-1.5" @click.stop><SupplierPreferenceActions :supplier-id="s.id" :compact="true" /><button v-if="false" class="cc-dir-card-quote" @click="quoteSupplier(s, $event)">QUOTE</button></div>
              </div>
            </div>

            <!-- Empty -->
            <div v-if="directoryRows.length === 0" class="text-center py-12 text-cc-text-secondary">
              <p class="text-lg">No suppliers match your filters.</p>
              <button @click="searchQuery = ''; tsdFilter = 'All'; clearFacets()" class="mt-2 text-cc-primary hover:underline">Clear all filters</button>
            </div>

            <!-- Load more (client-side over the full filtered population) -->
            <div v-if="directoryRows.length > pagedRows.length" class="flex items-center justify-center gap-3 mt-6 text-sm">
              <span class="text-cc-text-secondary">Showing {{ pagedRows.length }} of {{ directoryRows.length.toLocaleString() }}</span>
              <button @click="shown += 60" class="px-4 py-1.5 rounded-cc border border-cc-border-med hover:bg-cc-hover text-cc-text font-medium">Load more</button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<style scoped>
.cc-dir-facet-card {
  background: linear-gradient(180deg, var(--cc-surface) 0%, var(--cc-surface-2) 100%);
  border: 1px solid var(--cc-border);
  border-radius: 8px;
  padding: 4px 12px 8px;
  box-shadow: inset 0 1px 0 var(--cc-bezel-hi), inset 0 -1px 0 var(--cc-bezel-lo);
}
/* First facet group inside the card shouldn't show its top divider */
.cc-dir-facet-card :deep(.fg:first-of-type) { border-top: 0; }
.cc-dir-facets-top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 0 2px;
}
.cc-dir-facets-clear {
  background: transparent; border: 0; cursor: pointer;
  font-size: 11px; font-weight: 600; color: var(--cc-accent);
}
.cc-dir-facets-clear:hover { text-decoration: underline; }

/* Minimum-rating filter */
.cc-dir-rating-filter { padding: 8px 0 10px; border-bottom: 1px solid var(--cc-border); }
.cc-dir-rating-filter-h {
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--cc-text-2); margin-bottom: 7px;
}
.cc-dir-rating-opts { display: flex; flex-wrap: wrap; gap: 5px; }
.cc-dir-rating-opt {
  padding: 4px 9px; border-radius: 999px; cursor: pointer;
  background: var(--cc-surface); border: 1px solid var(--cc-border);
  font-size: 11.5px; font-weight: 600; color: var(--cc-text-2);
  transition: background 120ms, border-color 120ms, color 120ms;
}
.cc-dir-rating-opt:hover { border-color: var(--cc-accent); }
.cc-dir-rating-opt.is-on {
  background: var(--cc-accent); border-color: var(--cc-accent); color: var(--cc-btn-text, #fff);
}

/* Keep the list-row rating clear of the favorite/shield/block action buttons */
.cc-dir-row-actions { border-left: 1px solid var(--cc-border-soft); padding-left: 12px; }
</style>
